"use client";

import React, { useRef, useEffect } from "react";
import Editor from "@monaco-editor/react";
import * as monaco from "monaco-editor";
import { WandSparkles } from "lucide-react";
import { Button } from "@repo/ui";

interface JsonEditorProps {
  value: string;
  onChange?: (newValue: string) => void;
  disabled?: boolean;
  /**
   * Invoked on Cmd/Ctrl+Enter. When provided it takes precedence over the
   * default behavior of submitting the enclosing <form>. Mirrors the shared
   * @repo/ui Textarea submit contract. Shift+Enter still inserts a newline.
   */
  onSubmit?: () => void;
  /** Disable the Cmd/Ctrl+Enter submit shortcut. Defaults to true. */
  submitOnCmdEnter?: boolean;
}
const defaultJson = "{}";
export const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  disabled,
  onSubmit,
  submitOnCmdEnter = true,
}) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  // Keep the latest onSubmit in a ref: the Monaco command is registered once on
  // mount, but callers commonly pass a fresh inline callback every render.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  const handleEditorDidMount = (
    editor: monaco.editor.IStandaloneCodeEditor,
    // Use the monaco instance this editor belongs to. @monaco-editor/react loads
    // monaco from a CDN by default — a different instance than the imported
    // `monaco` package — so KeyMod/KeyCode constants must come from here or the
    // keybinding silently never fires.
    monacoInstance: typeof monaco,
  ) => {
    editorRef.current = editor;
    handleFormat();

    if (submitOnCmdEnter) {
      editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.Enter, () => {
        if (onSubmitRef.current) {
          onSubmitRef.current();
        } else {
          editor.getContainerDomNode()?.closest("form")?.requestSubmit();
        }
      });
    }
  };

  const handleEditorChange = (newValue: string | undefined) => {
    if (newValue === undefined) return;
    onChange?.(newValue);
  };

  const handleFormat = () => {
    if (editorRef.current) {
      editorRef.current.getAction("editor.action.formatDocument")?.run();
    }
  };

  const isInit = useRef(true);
  useEffect(() => {
    if (
      isInit.current &&
      editorRef.current?.getValue() === defaultJson &&
      value &&
      value.length > 0
    ) {
      editorRef.current.setValue(value);
      isInit.current = false;
    }
  }, [value]);

  return (
    <div className="relative rounded-md">
      <Editor
        height="300px"
        className="overflow-hidden rounded-md border border-gray-300"
        defaultLanguage="json"
        value={value || defaultJson}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
        options={{
          minimap: { enabled: false },
          formatOnPaste: false,
          formatOnType: false,
          wordWrap: "on",
          automaticLayout: true,
          readOnly: disabled,
          lineDecorationsWidth: 1, // Adjust width for spacing
        }}
      />
      <Button
        type="button"
        variant={"outline"}
        size={"icon"}
        className="absolute bottom-2 left-2 bg-white hover:bg-gray-100"
        onClick={handleFormat}
      >
        <WandSparkles size={14} className="text-gray-500" />
      </Button>
    </div>
  );
};
